import React, { Fragment, useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle,
  Form, FormGroup, Input, Label, Media, Row, UncontrolledDropdown, Card, CardBody,
} from "reactstrap";

import { MoreVertical, PlusCircle } from "react-feather";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import Select from "react-select";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Spinner } from "../../../../AbstractElements";
import CommonModal from "../../../UiKits/Modals/common/modal";
import { baseURL, variantsBaseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";
import useRbac from "../../../../Hooks/useRbac";
import { Breadcrumbs } from "../../../../AbstractElements";

const ORDER_STATUS_OPTIONS = [
  { value: "delivered", label: "Delivered" },
  { value: "accepted", label: "Accepted" },
  { value: "on-the-way", label: "On The Way" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "returned", label: "Returned" },
];

const initialForm = {
  title: "",
  description: "",
  badgeText: "",
  requiredOrders: 5,
  windowDays: 15,
  isRepeatable: true,
  giftVariant: "",
  giftsByTag: [],
  giftQuantity: 1,
  eligibleOrderStatuses: ["delivered"],
  requireCompletedPayment: true,
  termsAndConditions: "",
  isEnabled: false,
};

const RewardSettingsTable = () => {
  const { can } = useRbac();

  const [data, setData] = useState([]);
  const [variants, setVariants] = useState([]);
  const [tags, setTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleDelet, setToggleDelet] = useState(false);

  const [addModal, setAddModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState(initialForm);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem("token"));

  const resetForm = () => setForm(initialForm);

  const toggleAddModal = () => {
    setAddModal((p) => !p);
    setEditData(null);
    resetForm();
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleEligibleStatus = (value) => {
    setForm((prev) => {
      const exists = prev.eligibleOrderStatuses.includes(value);
      return {
        ...prev,
        eligibleOrderStatuses: exists
          ? prev.eligibleOrderStatuses.filter((s) => s !== value)
          : [...prev.eligibleOrderStatuses, value],
      };
    });
  };

  // ---------- API ----------
  const fetchTags = async () => {
    try {
      const res = await axios.get(`${baseURL}/api/tags`, {
        headers: { Authorization: `${getToken()}` },
      });
      setTags(res?.data?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const addGiftTagRow = () => {
    setForm((prev) => ({
      ...prev,
      giftsByTag: [...prev.giftsByTag, { tag: "", variant: "" }],
    }));
  };

  const removeGiftTagRow = (index) => {
    setForm((prev) => ({
      ...prev,
      giftsByTag: prev.giftsByTag.filter((_, i) => i !== index),
    }));
  };

  const updateGiftTagRow = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      giftsByTag: prev.giftsByTag.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const fetchVariants = async () => {
    try {
      const res = await axios.get(`${variantsBaseURL}/variants`, {
        headers: { Authorization: `${getToken()}` },
      });
      setVariants(res?.data?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchRewardSettings = async (page = 1, limit = perPage) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseURL}/api/admin/reward-settings?page=${page}&limit=${limit}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setData(res.data.data || []);
      setTotalRows(res.data.total || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const postRewardSetting = async () => {
    try {
      await axios.post(`${baseURL}/api/admin/reward-settings`, form, {
        headers: { Authorization: `${getToken()}` },
      });
      toggleAddModal();
      fetchRewardSettings(1);
      Swal.fire({ title: "Reward Program Added!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      console.log(err);
      Swal.fire({ title: "Failed to add reward program", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  const updateRewardSetting = async (id) => {
    try {
      await axios.patch(`${baseURL}/api/admin/reward-settings/${id}`, form, {
        headers: { Authorization: `${getToken()}` },
      });
      toggleAddModal();
      fetchRewardSettings(1);
      Swal.fire({ title: "Reward Program Updated!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      console.log(err);
    }
  };

  const toggleRewardStatus = async () => {
    try {
      const newStatus = !editData.isEnabled;
      await axios.patch(
        `${baseURL}/api/admin/reward-settings/${editData._id}/status`,
        { isEnabled: newStatus },
        { headers: { Authorization: `${getToken()}` } }
      );
      setStatusModal(false);
      setEditData(null);
      fetchRewardSettings(1);
    } catch (err) {
      console.log(err);
    }
  };

  const deleteRewardSetting = async (id) => {
    const result = await Swal.fire({
      title: "Delete this reward program?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d3178a",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${baseURL}/api/admin/reward-settings/${id}`, {
        headers: { Authorization: `${getToken()}` },
      });
      fetchRewardSettings(1);
      Swal.fire({ title: "Reward Program Deleted!", icon: "success", confirmButtonColor: "#d3178a" });
    } catch (err) {
      console.log(err);
      Swal.fire({ title: "Failed to delete reward program", icon: "error", confirmButtonColor: "#d3178a" });
    }
  };

  // ---------- pagination ----------
  const handlePageChange = (page) => fetchRewardSettings(page, perPage);
  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchRewardSettings(page, newPerPage);
  };

  useEffect(() => {
    fetchRewardSettings(1);
    fetchVariants();
    fetchTags();
    // eslint-disable-next-line
  }, []);

  // ---------- handlers ----------
  const handleSubmit = () => {
    if (!form.title) {
      Swal.fire({ title: "Title is required", icon: "warning", confirmButtonColor: "#d3178a" });
      return;
    }
    if (form.eligibleOrderStatuses.length === 0) {
      Swal.fire({ title: "Select at least one eligible order status", icon: "warning", confirmButtonColor: "#d3178a" });
      return;
    }
    if (editData) updateRewardSetting(editData._id);
    else postRewardSetting();
  };

  const openEdit = (row) => {
    setEditData(row);
    setForm({
      title: row.title || "",
      description: row.description || "",
      badgeText: row.badgeText || "",
      requiredOrders: row.requiredOrders ?? 5,
      windowDays: row.windowDays ?? 15,
      isRepeatable: row.isRepeatable ?? true,
      giftVariant: row.giftVariant?._id || row.giftVariant || "",
      giftsByTag: (row.giftsByTag || []).map((entry) => ({
        tag: entry.tag?._id || entry.tag || "",
        variant: entry.variant?._id || entry.variant || "",
      })),
      giftQuantity: row.giftQuantity ?? 1,
      eligibleOrderStatuses: row.eligibleOrderStatuses?.length ? row.eligibleOrderStatuses : ["delivered"],
      requireCompletedPayment: row.requireCompletedPayment ?? true,
      termsAndConditions: row.termsAndConditions || "",
      isEnabled: row.isEnabled || false,
    });
    setAddModal(true);
  };

  const handleRowSelected = useCallback((state) => setSelectedRows(state.selectedRows), []);

  // ---------- columns ----------
  const columns = [
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
      center: true,
      cell: (row) => <span>{row.title}</span>,
    },
    {
      name: "Required Orders",
      selector: (row) => row.requiredOrders,
      sortable: true,
      center: true,
    },
    {
      name: "Window (days)",
      selector: (row) => row.windowDays,
      sortable: true,
      center: true,
    },
    {
      name: "Gift Variant",
      selector: (row) => row.giftVariant?.variantName,
      center: true,
      cell: (row) => (
        <div style={{ textAlign: "center" }}>
          <div>{row.giftVariant?.variantName || "No default set"}</div>
          {row.giftsByTag?.length > 0 && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              +{row.giftsByTag.length} tag-based gift{row.giftsByTag.length > 1 ? "s" : ""}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Repeatable",
      selector: (row) => row.isRepeatable,
      center: true,
      cell: (row) => (row.isRepeatable ? "Yes" : "No"),
    },
    {
      name: "Status",
      selector: (row) => row.isEnabled,
      sortable: true,
      center: true,
      cell: (row) => (
        <p className={`text-${row.isEnabled ? "[#008800]" : "[#ff0000]"} mb-0`}>
          {row.isEnabled ? "Active" : "In-Active"}
        </p>
      ),
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      center: true,
      cell: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
    },
    {
      name: "Actions",
      center: true,
      omit: !can('reward.update'),
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            {can('reward.update') && <DropdownItem onClick={() => openEdit(row)}>
              Edit <FaPen />
            </DropdownItem>}
            {can('reward.change_status') && <DropdownItem
              onClick={() => {
                setEditData(row);
                setStatusModal(true);
              }}
            >
              {row.isEnabled ? "Deactivate" : "Activate"}
            </DropdownItem>}
            {can('reward.delete') && <DropdownItem
              className="delete_item"
              onClick={() => deleteRewardSetting(row._id)}
            >
              Delete <FaTrashAlt />
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const filtered = data.filter((it) =>
    `${it.title} ${it.description}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const variantOptions = variants.map((v) => ({ value: v._id, label: v.variantName }));

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Reward Programs" parent="Customer Rewards" parentLink="/reward" title="Reward Programs" />
      <Container fluid={true} style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm='12'>
            <Card>
              <CardBody style={{ padding: '15px' }}>

                <Fragment>
                  <Row xxl={12} className="pb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="fw-bolder">Reward Programs</h5>
                      <div className="file-content">
                        <Media>
                          <div className="mb-0 form-group position-relative search_outer d-flex align-items-center">
                            <i className="fa fa-search"></i>
                            <input
                              className="form-control border-0"
                              value={searchTerm}
                              onChange={handleSearch}
                              type="text"
                              placeholder="Search..."
                            />
                          </div>
                          {can('reward.create') && (
                            <Media body className="text-end ms-3">
                              <Button className="btn btn-primary d-flex align-items-center" onClick={toggleAddModal}>
                                <PlusCircle />
                                Add Reward Program
                              </Button>
                            </Media>
                          )}
                        </Media>
                      </div>
                    </div>
                  </Row>

                  {data.length !== 0 ? (
                    <DataTable
                      data={filtered}
                      columns={columns}
                      pagination
                      paginationServer
                      progressPending={loading}
                      progressComponent={<Loader />}
                      paginationTotalRows={totalRows}
                      onChangeRowsPerPage={handlePerRowsChange}
                      onChangePage={handlePageChange}
                      onSelectedRowsChange={handleRowSelected}
                      clearSelectedRows={toggleDelet}
                    />
                  ) : (
                    <Col xxl="12" key="loader" className="flex justify-center items-center">
                      <div className="loader-box">
                        <Spinner />
                      </div>
                    </Col>
                  )}

                  {/* Add / Edit Modal */}
                  <CommonModal
                    isOpen={addModal}
                    title={editData ? "Edit Reward Program" : "Add New Reward Program"}
                    className="store_modal"
                    toggler={toggleAddModal}
                    size="lg"
                  >
                    <Container>
                      <Form>
                        <Col xxl={12}>
                          <FormGroup>
                            <Label className="font-medium text-base">
                              Title <span className="text-danger">*</span>
                            </Label>
                            <Input
                              value={form.title}
                              onChange={(e) => handleField("title", e.target.value)}
                              placeholder="e.g. Bottle Stamps"
                            />
                          </FormGroup>
                        </Col>

                        <Col xxl={12}>
                          <FormGroup>
                            <Label className="font-medium text-base">Customer-facing Description</Label>
                            <Input
                              value={form.description}
                              onChange={(e) => handleField("description", e.target.value)}
                              placeholder="e.g. Place 5 orders in 15 days and get a free gift."
                            />
                          </FormGroup>
                        </Col>

                        <Row>
                          <Col xxl={6} md={6}>
                            <FormGroup>
                              <Label className="font-medium text-base">Badge Text</Label>
                              <Input
                                value={form.badgeText}
                                onChange={(e) => handleField("badgeText", e.target.value)}
                                placeholder="e.g. BOTTLE STAMPS"
                              />
                            </FormGroup>
                          </Col>
                          <Col xxl={6} md={6}>
                            <FormGroup>
                              <Label className="font-medium text-base">Default Gift (no tag match)</Label>
                              <Select
                                options={variantOptions}
                                value={variantOptions.find((o) => o.value === form.giftVariant) || null}
                                onChange={(selected) => handleField("giftVariant", selected?.value || "")}
                                placeholder="Select Variant"
                                isClearable
                                isSearchable
                              />
                              <small className="text-[#1D2433A6]">
                                Used when a customer has no tag matching a mapping below.
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Col xxl={12}>
                          <FormGroup>
                            <Label className="font-medium text-base">Gift by Customer Tag</Label>
                            <div className="border rounded p-2">
                              {form.giftsByTag.map((row, index) => (
                                <Row key={index} className="align-items-center mb-2">
                                  <Col xxl={5} md={5}>
                                    <Input
                                      type="select"
                                      value={row.tag}
                                      onChange={(e) => updateGiftTagRow(index, "tag", e.target.value)}
                                    >
                                      <option value="">Select Tag</option>
                                      {tags.map((t) => (
                                        <option key={t._id} value={t._id}>{t.display_name}</option>
                                      ))}
                                    </Input>
                                  </Col>
                                  <Col xxl={5} md={5}>
                                    <Select
                                      options={variantOptions}
                                      value={variantOptions.find((o) => o.value === row.variant) || null}
                                      onChange={(selected) => updateGiftTagRow(index, "variant", selected?.value || "")}
                                      placeholder="Select Variant"
                                      isClearable
                                      isSearchable
                                    />
                                  </Col>
                                  <Col xxl={2} md={2}>
                                    <Button
                                      type="button"
                                      color="danger"
                                      outline
                                      size="sm"
                                      onClick={() => removeGiftTagRow(index)}
                                    >
                                      Remove
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                type="button"
                                className="btn btn-primary btn-sm d-flex align-items-center mt-1"
                                onClick={addGiftTagRow}
                              >
                                <PlusCircle size={14} className="me-1" />
                                Add Tag → Gift Mapping
                              </Button>
                            </div>
                            <small className="text-[#1D2433A6]">
                              If a customer matches more than one tag below, the first matching row wins.
                            </small>
                          </FormGroup>
                        </Col>

                        <Row>
                          <Col xxl={4} md={4}>
                            <FormGroup>
                              <Label className="font-medium text-base">Required Orders</Label>
                              <Input
                                type="number"
                                min={1}
                                value={form.requiredOrders}
                                onChange={(e) => handleField("requiredOrders", parseInt(e.target.value) || 1)}
                              />
                            </FormGroup>
                          </Col>
                          <Col xxl={4} md={4}>
                            <FormGroup>
                              <Label className="font-medium text-base">Window (days)</Label>
                              <Input
                                type="number"
                                min={1}
                                value={form.windowDays}
                                onChange={(e) => handleField("windowDays", parseInt(e.target.value) || 1)}
                              />
                            </FormGroup>
                          </Col>
                          <Col xxl={4} md={4}>
                            <FormGroup>
                              <Label className="font-medium text-base">Gift Quantity</Label>
                              <Input
                                type="number"
                                min={1}
                                value={form.giftQuantity}
                                onChange={(e) => handleField("giftQuantity", parseInt(e.target.value) || 1)}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Col xxl={12}>
                          <FormGroup>
                            <Label className="font-medium text-base">Eligible Order Statuses</Label>
                            <div className="d-flex flex-wrap gap-3 border rounded p-2">
                              {ORDER_STATUS_OPTIONS.map((opt) => (
                                <label key={opt.value} className="flex items-center gap-1" style={{ marginRight: "12px" }}>
                                  <input
                                    type="checkbox"
                                    checked={form.eligibleOrderStatuses.includes(opt.value)}
                                    onChange={() => toggleEligibleStatus(opt.value)}
                                  />{" "}
                                  {opt.label}
                                </label>
                              ))}
                            </div>
                            <small className="text-[#1D2433A6]">
                              Only orders reaching one of these statuses count toward the reward.
                            </small>
                          </FormGroup>
                        </Col>

                        <Col xxl={12}>
                          <FormGroup className="mb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!form.requireCompletedPayment}
                                onChange={(e) => handleField("requireCompletedPayment", e.target.checked)}
                                className="w-4 h-4 cursor-pointer accent-[#d3178a]"
                              />
                              <span>Only count orders with completed payment</span>
                            </label>
                          </FormGroup>
                        </Col>

                        <Col xxl={12}>
                          <FormGroup className="mb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!form.isRepeatable}
                                onChange={(e) => handleField("isRepeatable", e.target.checked)}
                                className="w-4 h-4 cursor-pointer accent-[#d3178a]"
                              />
                              <span>Repeatable — customer can earn this reward again after claiming</span>
                            </label>
                          </FormGroup>
                        </Col>

                        <Col xxl={12}>
                          <FormGroup>
                            <Label className="font-medium text-base">Terms and Conditions</Label>
                            <Input
                              type="textarea"
                              rows={3}
                              value={form.termsAndConditions}
                              onChange={(e) => handleField("termsAndConditions", e.target.value)}
                              placeholder="e.g. 18+ only. ID checked at delivery. One reward per customer per window."
                            />
                          </FormGroup>
                        </Col>

                        <Col xxl={12}>
                          <FormGroup>
                            <Label className="font-medium text-base">Status</Label>
                            <div className="flex flex-row gap-4">
                              <label className="flex items-center gap-1">
                                <input
                                  type="radio"
                                  checked={form.isEnabled === true}
                                  onChange={() => handleField("isEnabled", true)}
                                />
                                Active
                              </label>
                              <label className="flex items-center gap-1">
                                <input
                                  type="radio"
                                  checked={form.isEnabled === false}
                                  onChange={() => handleField("isEnabled", false)}
                                />
                                Inactive
                              </label>
                            </div>
                            <small className="text-[#1D2433A6]">
                              Activating this reward program will deactivate the currently active one.
                            </small>
                          </FormGroup>
                        </Col>

                        <Row>
                          <Col xxl={12} className="text-end">
                            <Button
                              type="button"
                              onClick={handleSubmit}
                              className="cursor-pointer bg-[#d3178a] font-medium w-40 mt-4 px-2 py-2 rounded-2xl text-white flex justify-center items-center"
                              style={{ border: "none" }}
                            >
                              {editData ? "Update Program" : "Save Program"}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </Container>
                  </CommonModal>

                  {/* Status Toggle Confirm */}
                  <CommonModal
                    isOpen={statusModal}
                    title=""
                    toggler={() => setStatusModal(false)}
                    size="md"
                  >
                    <Container className="flex flex-col justify-center items-center">
                      <p>
                        {editData?.isEnabled
                          ? "Deactivate this reward program?"
                          : "Activate this reward program? Any other active program will be deactivated."}
                      </p>
                      <div className="flex flex-row space-x-8">
                        <div
                          onClick={() => setStatusModal(false)}
                          className="border-1 border-[#ff0000] px-2 py-2 text-[#ff0000] rounded-xl cursor-pointer"
                        >
                          Cancel
                        </div>
                        <div
                          onClick={toggleRewardStatus}
                          className="bg-[#d3178a] px-4 py-2 text-white rounded-xl cursor-pointer"
                        >
                          Yes
                        </div>
                      </div>
                    </Container>
                  </CommonModal>
                </Fragment>

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default RewardSettingsTable;


// import React, { Fragment, useCallback, useEffect, useState } from "react";
// import DataTable from "react-data-table-component";
// import {
//   Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle,
//   Form, FormGroup, Input, Label, Media, Row, UncontrolledDropdown, Card, CardBody,
// } from "reactstrap";
// import { MoreVertical, PlusCircle } from "react-feather";
// import { FaPen, FaTrashAlt } from "react-icons/fa";
// import axios from "axios";
// import moment from "moment";
// import Swal from "sweetalert2";
// import { Spinner } from "../../../../AbstractElements";
// import CommonModal from "../../../UiKits/Modals/common/modal";
// import { baseURL, productBaseURL } from "../../../../Services/api/baseURL";
// import Loader from "../../../Loader/Loader";
// import useRbac from "../../../../Hooks/useRbac";

// const ORDER_STATUS_OPTIONS = [
//   { value: "delivered", label: "Delivered" },
//   { value: "accepted", label: "Accepted" },
//   { value: "on-the-way", label: "On The Way" },
//   { value: "pending", label: "Pending" },
//   { value: "rejected", label: "Rejected" },
//   { value: "cancelled", label: "Cancelled" },
//   { value: "returned", label: "Returned" },
// ];

// const initialForm = {
//   title: "",
//   description: "",
//   badgeText: "",
//   requiredOrders: 5,
//   windowDays: 15,
//   isRepeatable: true,
//   giftProduct: "",
//   giftQuantity: 1,
//   eligibleOrderStatuses: ["delivered"],
//   requireCompletedPayment: true,
//   termsAndConditions: "",
//   isEnabled: false,
// };

// const RewardSettingsTable = () => {
//   const { can } = useRbac();

//   const [data, setData] = useState([]);
//   const [products, setProducts] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedRows, setSelectedRows] = useState([]);
//   const [toggleDelet, setToggleDelet] = useState(false);

//   const [addModal, setAddModal] = useState(false);
//   const [statusModal, setStatusModal] = useState(false);
//   const [editData, setEditData] = useState(null);
//   const [form, setForm] = useState(initialForm);

//   const [totalRows, setTotalRows] = useState(0);
//   const [perPage, setPerPage] = useState(10);
//   const [loading, setLoading] = useState(false);

//   const getToken = () => JSON.parse(localStorage.getItem("token"));

//   const resetForm = () => setForm(initialForm);

//   const toggleAddModal = () => {
//     setAddModal((p) => !p);
//     setEditData(null);
//     resetForm();
//   };

//   const handleSearch = (e) => setSearchTerm(e.target.value);

//   const handleField = (key, value) =>
//     setForm((prev) => ({ ...prev, [key]: value }));

//   const toggleEligibleStatus = (value) => {
//     setForm((prev) => {
//       const exists = prev.eligibleOrderStatuses.includes(value);
//       return {
//         ...prev,
//         eligibleOrderStatuses: exists
//           ? prev.eligibleOrderStatuses.filter((s) => s !== value)
//           : [...prev.eligibleOrderStatuses, value],
//       };
//     });
//   };

//   // ---------- API ----------
//   const fetchProducts = async () => {
//     try {
//       const res = await axios.get(`${productBaseURL}/products/get-products`, {
//         headers: { Authorization: `${getToken()}` },
//       });
//       setProducts(res?.data?.data || []);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   const fetchRewardSettings = async (page = 1, limit = perPage) => {
//     setLoading(true);
//     try {
//       const res = await axios.get(
//         `${baseURL}/api/admin/reward-settings?page=${page}&limit=${limit}`,
//         { headers: { Authorization: `${getToken()}` } }
//       );
//       setData(res.data.data || []);
//       setTotalRows(res.data.total || 0);
//     } catch (err) {
//       console.log(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const postRewardSetting = async () => {
//     try {
//       await axios.post(`${baseURL}/api/admin/reward-settings`, form, {
//         headers: { Authorization: `${getToken()}` },
//       });
//       toggleAddModal();
//       fetchRewardSettings(1);
//       Swal.fire({ title: "Reward Program Added!", icon: "success", confirmButtonColor: "#d3178a" });
//     } catch (err) {
//       console.log(err);
//       Swal.fire({ title: "Failed to add reward program", icon: "error", confirmButtonColor: "#d3178a" });
//     }
//   };

//   const updateRewardSetting = async (id) => {
//     try {
//       await axios.patch(`${baseURL}/api/admin/reward-settings/${id}`, form, {
//         headers: { Authorization: `${getToken()}` },
//       });
//       toggleAddModal();
//       fetchRewardSettings(1);
//       Swal.fire({ title: "Reward Program Updated!", icon: "success", confirmButtonColor: "#d3178a" });
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   const toggleRewardStatus = async () => {
//     try {
//       const newStatus = !editData.isEnabled;
//       await axios.patch(
//         `${baseURL}/api/admin/reward-settings/${editData._id}/status`,
//         { isEnabled: newStatus },
//         { headers: { Authorization: `${getToken()}` } }
//       );
//       setStatusModal(false);
//       setEditData(null);
//       fetchRewardSettings(1);
//     } catch (err) {
//       console.log(err);
//     }
//   };

//   const deleteRewardSetting = async (id) => {
//     const result = await Swal.fire({
//       title: "Delete this reward program?",
//       text: "This action cannot be undone.",
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#d3178a",
//       cancelButtonColor: "#6c757d",
//       confirmButtonText: "Yes, delete it",
//       cancelButtonText: "Cancel",
//     });

//     if (!result.isConfirmed) return;

//     try {
//       await axios.delete(`${baseURL}/api/admin/reward-settings/${id}`, {
//         headers: { Authorization: `${getToken()}` },
//       });
//       fetchRewardSettings(1);
//       Swal.fire({ title: "Reward Program Deleted!", icon: "success", confirmButtonColor: "#d3178a" });
//     } catch (err) {
//       console.log(err);
//       Swal.fire({ title: "Failed to delete reward program", icon: "error", confirmButtonColor: "#d3178a" });
//     }
//   };

//   // ---------- pagination ----------
//   const handlePageChange = (page) => fetchRewardSettings(page, perPage);
//   const handlePerRowsChange = async (newPerPage, page) => {
//     setPerPage(newPerPage);
//     fetchRewardSettings(page, newPerPage);
//   };

//   useEffect(() => {
//     fetchRewardSettings(1);
//     fetchProducts();
//     // eslint-disable-next-line
//   }, []);

//   // ---------- handlers ----------
//   const handleSubmit = () => {
//     if (!form.title) {
//       Swal.fire({ title: "Title is required", icon: "warning", confirmButtonColor: "#d3178a" });
//       return;
//     }
//     if (form.eligibleOrderStatuses.length === 0) {
//       Swal.fire({ title: "Select at least one eligible order status", icon: "warning", confirmButtonColor: "#d3178a" });
//       return;
//     }
//     if (editData) updateRewardSetting(editData._id);
//     else postRewardSetting();
//   };

//   const openEdit = (row) => {
//     setEditData(row);
//     setForm({
//       title: row.title || "",
//       description: row.description || "",
//       badgeText: row.badgeText || "",
//       requiredOrders: row.requiredOrders ?? 5,
//       windowDays: row.windowDays ?? 15,
//       isRepeatable: row.isRepeatable ?? true,
//       giftProduct: row.giftProduct?._id || row.giftProduct || "",
//       giftQuantity: row.giftQuantity ?? 1,
//       eligibleOrderStatuses: row.eligibleOrderStatuses?.length ? row.eligibleOrderStatuses : ["delivered"],
//       requireCompletedPayment: row.requireCompletedPayment ?? true,
//       termsAndConditions: row.termsAndConditions || "",
//       isEnabled: row.isEnabled || false,
//     });
//     setAddModal(true);
//   };

//   const handleRowSelected = useCallback((state) => setSelectedRows(state.selectedRows), []);

//   // ---------- columns ----------
//   const columns = [
//     {
//       name: "Title",
//       selector: (row) => row.title,
//       sortable: true,
//       center: true,
//       cell: (row) => <span>{row.title}</span>,
//     },
//     {
//       name: "Required Orders",
//       selector: (row) => row.requiredOrders,
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "Window (days)",
//       selector: (row) => row.windowDays,
//       sortable: true,
//       center: true,
//     },
//     {
//       name: "Gift Product",
//       selector: (row) => row.giftProduct?.productName,
//       center: true,
//       cell: (row) => row.giftProduct?.productName || "Not set",
//     },
//     {
//       name: "Repeatable",
//       selector: (row) => row.isRepeatable,
//       center: true,
//       cell: (row) => (row.isRepeatable ? "Yes" : "No"),
//     },
//     {
//       name: "Status",
//       selector: (row) => row.isEnabled,
//       sortable: true,
//       center: true,
//       cell: (row) => (
//         <p className={`text-${row.isEnabled ? "[#008800]" : "[#ff0000]"} mb-0`}>
//           {row.isEnabled ? "Active" : "In-Active"}
//         </p>
//       ),
//     },
//     {
//       name: "Created",
//       selector: (row) => row.createdAt,
//       sortable: true,
//       center: true,
//       cell: (row) => moment(row.createdAt).format("DD/MM/YYYY"),
//     },
//     {
//       name: "Actions",
//       center: true,
//       omit: !can('reward.update'),
//       cell: (row) => (
//         <UncontrolledDropdown className="action_dropdown">
//           <DropdownToggle className="action_btn">
//             <MoreVertical color="#000" size={16} />
//           </DropdownToggle>
//           <DropdownMenu>
//             {can('reward.update') && <DropdownItem onClick={() => openEdit(row)}>
//               Edit <FaPen />
//             </DropdownItem>}
//             {can('reward.change_status') && <DropdownItem
//               onClick={() => {
//                 setEditData(row);
//                 setStatusModal(true);
//               }}
//             >
//               {row.isEnabled ? "Deactivate" : "Activate"}
//             </DropdownItem>}
//             {can('reward.delete') && <DropdownItem
//               className="delete_item"
//               onClick={() => deleteRewardSetting(row._id)}
//             >
//               Delete <FaTrashAlt />
//             </DropdownItem>}
//           </DropdownMenu>
//         </UncontrolledDropdown>
//       ),
//     },
//   ];

//   const filtered = data.filter((it) =>
//     `${it.title} ${it.description}`.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   return (
//     <Fragment>
//       <Container fluid={true} style={{ paddingTop: '30px' }}>
//         <Row>
//           <Col sm='12'>
//             <Card>
//               <CardBody style={{ padding: '15px' }}>
//                 <Fragment>
//                   <Row xxl={12} className="pb-4">
//                     <div className="d-flex justify-content-between align-items-center">
//                       <h5 className="fw-bolder">Reward Programs</h5>
//                       <div className="file-content">
//                         <Media>
//                           <div className="mb-0 form-group position-relative search_outer d-flex align-items-center">
//                             <i className="fa fa-search"></i>
//                             <input
//                               className="form-control border-0"
//                               value={searchTerm}
//                               onChange={handleSearch}
//                               type="text"
//                               placeholder="Search..."
//                             />
//                           </div>
//                           {can('reward.create') && (
//                             <Media body className="text-end ms-3">
//                               <Button className="btn btn-primary d-flex align-items-center" onClick={toggleAddModal}>
//                                 <PlusCircle />
//                                 Add Reward Program
//                               </Button>
//                             </Media>
//                           )}
//                         </Media>
//                       </div>
//                     </div>
//                   </Row>

//                   {data.length !== 0 ? (
//                     <DataTable
//                       data={filtered}
//                       columns={columns}
//                       pagination
//                       paginationServer
//                       progressPending={loading}
//                       progressComponent={<Loader />}
//                       paginationTotalRows={totalRows}
//                       onChangeRowsPerPage={handlePerRowsChange}
//                       onChangePage={handlePageChange}
//                       onSelectedRowsChange={handleRowSelected}
//                       clearSelectedRows={toggleDelet}
//                     />
//                   ) : (
//                     <Col xxl="12" key="loader" className="flex justify-center items-center">
//                       <div className="loader-box">
//                         <Spinner />
//                       </div>
//                     </Col>
//                   )}

//                   {/* Add / Edit Modal */}
//                   <CommonModal
//                     isOpen={addModal}
//                     title={editData ? "Edit Reward Program" : "Add New Reward Program"}
//                     className="store_modal"
//                     toggler={toggleAddModal}
//                     size="lg"
//                   >
//                     <Container>
//                       <Form>
//                         <Col xxl={12}>
//                           <FormGroup>
//                             <Label className="font-medium text-base">
//                               Title <span className="text-danger">*</span>
//                             </Label>
//                             <Input
//                               value={form.title}
//                               onChange={(e) => handleField("title", e.target.value)}
//                               placeholder="e.g. Bottle Stamps"
//                             />
//                           </FormGroup>
//                         </Col>

//                         <Col xxl={12}>
//                           <FormGroup>
//                             <Label className="font-medium text-base">Customer-facing Description</Label>
//                             <Input
//                               value={form.description}
//                               onChange={(e) => handleField("description", e.target.value)}
//                               placeholder="e.g. Place 5 orders in 15 days and get a free gift."
//                             />
//                           </FormGroup>
//                         </Col>

//                         <Row>
//                           <Col xxl={6} md={6}>
//                             <FormGroup>
//                               <Label className="font-medium text-base">Badge Text</Label>
//                               <Input
//                                 value={form.badgeText}
//                                 onChange={(e) => handleField("badgeText", e.target.value)}
//                                 placeholder="e.g. BOTTLE STAMPS"
//                               />
//                             </FormGroup>
//                           </Col>
//                           <Col xxl={6} md={6}>
//                             <FormGroup>
//                               <Label className="font-medium text-base">Gift Product</Label>
//                               <Input
//                                 type="select"
//                                 value={form.giftProduct}
//                                 onChange={(e) => handleField("giftProduct", e.target.value)}
//                               >
//                                 <option value="">Select Product</option>
//                                 {products.map((p) => (
//                                   <option key={p._id} value={p._id}>{p.productName}</option>
//                                 ))}
//                               </Input>
//                             </FormGroup>
//                           </Col>
//                         </Row>

//                         <Row>
//                           <Col xxl={4} md={4}>
//                             <FormGroup>
//                               <Label className="font-medium text-base">Required Orders</Label>
//                               <Input
//                                 type="number"
//                                 min={1}
//                                 value={form.requiredOrders}
//                                 onChange={(e) => handleField("requiredOrders", parseInt(e.target.value) || 1)}
//                               />
//                             </FormGroup>
//                           </Col>
//                           <Col xxl={4} md={4}>
//                             <FormGroup>
//                               <Label className="font-medium text-base">Window (days)</Label>
//                               <Input
//                                 type="number"
//                                 min={1}
//                                 value={form.windowDays}
//                                 onChange={(e) => handleField("windowDays", parseInt(e.target.value) || 1)}
//                               />
//                             </FormGroup>
//                           </Col>
//                           <Col xxl={4} md={4}>
//                             <FormGroup>
//                               <Label className="font-medium text-base">Gift Quantity</Label>
//                               <Input
//                                 type="number"
//                                 min={1}
//                                 value={form.giftQuantity}
//                                 onChange={(e) => handleField("giftQuantity", parseInt(e.target.value) || 1)}
//                               />
//                             </FormGroup>
//                           </Col>
//                         </Row>

//                         <Col xxl={12}>
//                           <FormGroup>
//                             <Label className="font-medium text-base">Eligible Order Statuses</Label>
//                             <div className="d-flex flex-wrap gap-3 border rounded p-2">
//                               {ORDER_STATUS_OPTIONS.map((opt) => (
//                                 <label key={opt.value} className="flex items-center gap-1" style={{ marginRight: "12px" }}>
//                                   <input
//                                     type="checkbox"
//                                     checked={form.eligibleOrderStatuses.includes(opt.value)}
//                                     onChange={() => toggleEligibleStatus(opt.value)}
//                                   />{" "}
//                                   {opt.label}
//                                 </label>
//                               ))}
//                             </div>
//                             <small className="text-[#1D2433A6]">
//                               Only orders reaching one of these statuses count toward the reward.
//                             </small>
//                           </FormGroup>
//                         </Col>

//                         <Col xxl={12}>
//                           <FormGroup className="mb-2">
//                             <label className="flex items-center gap-2 cursor-pointer">
//                               <input
//                                 type="checkbox"
//                                 checked={!!form.requireCompletedPayment}
//                                 onChange={(e) => handleField("requireCompletedPayment", e.target.checked)}
//                                 className="w-4 h-4 cursor-pointer accent-[#d3178a]"
//                               />
//                               <span>Only count orders with completed payment</span>
//                             </label>
//                           </FormGroup>
//                         </Col>

//                         <Col xxl={12}>
//                           <FormGroup className="mb-2">
//                             <label className="flex items-center gap-2 cursor-pointer">
//                               <input
//                                 type="checkbox"
//                                 checked={!!form.isRepeatable}
//                                 onChange={(e) => handleField("isRepeatable", e.target.checked)}
//                                 className="w-4 h-4 cursor-pointer accent-[#d3178a]"
//                               />
//                               <span>Repeatable — customer can earn this reward again after claiming</span>
//                             </label>
//                           </FormGroup>
//                         </Col>

//                         <Col xxl={12}>
//                           <FormGroup>
//                             <Label className="font-medium text-base">Terms and Conditions</Label>
//                             <Input
//                               type="textarea"
//                               rows={3}
//                               value={form.termsAndConditions}
//                               onChange={(e) => handleField("termsAndConditions", e.target.value)}
//                               placeholder="e.g. 18+ only. ID checked at delivery. One reward per customer per window."
//                             />
//                           </FormGroup>
//                         </Col>

//                         <Col xxl={12}>
//                           <FormGroup>
//                             <Label className="font-medium text-base">Status</Label>
//                             <div className="flex flex-row gap-4">
//                               <label className="flex items-center gap-1">
//                                 <input
//                                   type="radio"
//                                   checked={form.isEnabled === true}
//                                   onChange={() => handleField("isEnabled", true)}
//                                 />
//                                 Active
//                               </label>
//                               <label className="flex items-center gap-1">
//                                 <input
//                                   type="radio"
//                                   checked={form.isEnabled === false}
//                                   onChange={() => handleField("isEnabled", false)}
//                                 />
//                                 Inactive
//                               </label>
//                             </div>
//                             <small className="text-[#1D2433A6]">
//                               Activating this reward program will deactivate the currently active one.
//                             </small>
//                           </FormGroup>
//                         </Col>

//                         <Row>
//                           <Col xxl={12} className="text-end">
//                             <Button
//                               type="button"
//                               onClick={handleSubmit}
//                               className="cursor-pointer bg-[#d3178a] font-medium w-40 mt-4 px-2 py-2 rounded-2xl text-white flex justify-center items-center"
//                               style={{ border: "none" }}
//                             >
//                               {editData ? "Update Program" : "Save Program"}
//                             </Button>
//                           </Col>
//                         </Row>
//                       </Form>
//                     </Container>
//                   </CommonModal>

//                   {/* Status Toggle Confirm */}
//                   <CommonModal
//                     isOpen={statusModal}
//                     title=""
//                     toggler={() => setStatusModal(false)}
//                     size="md"
//                   >
//                     <Container className="flex flex-col justify-center items-center">
//                       <p>
//                         {editData?.isEnabled
//                           ? "Deactivate this reward program?"
//                           : "Activate this reward program? Any other active program will be deactivated."}
//                       </p>
//                       <div className="flex flex-row space-x-8">
//                         <div
//                           onClick={() => setStatusModal(false)}
//                           className="border-1 border-[#ff0000] px-2 py-2 text-[#ff0000] rounded-xl cursor-pointer"
//                         >
//                           Cancel
//                         </div>
//                         <div
//                           onClick={toggleRewardStatus}
//                           className="bg-[#d3178a] px-4 py-2 text-white rounded-xl cursor-pointer"
//                         >
//                           Yes
//                         </div>
//                       </div>
//                     </Container>
//                   </CommonModal>
//                 </Fragment>
//               </CardBody>
//             </Card>
//           </Col>
//         </Row>
//       </Container>
//     </Fragment>
//   );
// };

// export default RewardSettingsTable;